import { useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Building2, Mail, Phone, Hash, MapPin, User, ShieldCheck, Camera, Loader2, BookOpen } from 'lucide-react';
import { motion } from 'framer-motion';
import { fetchWithAuth } from '../../utils/api';
import { CustomSelect } from '../../components/CustomSelect';
import { CustomDatePickerFixed } from '../../components/CustomDatePickerFixed';
import { formatPincode } from '../../utils/formatters';
import { SubscriptionTab } from './SubscriptionTab';
import { compressImage } from '../../utils/image';

const Swatches = ({ theme, size = 14 }) => (
  <span className="inline-flex items-center gap-1.5" aria-hidden="true">
    <span style={{ width: size, height: size, borderRadius: 999, background: theme.primary }} />
    <span style={{ width: size, height: size, borderRadius: 999, background: theme.secondary, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.06)' }} />
    <span style={{ width: size, height: size, borderRadius: 999, background: theme.tertiary, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.06)' }} />
  </span>
);

Swatches.propTypes = {
  theme: PropTypes.shape({
    primary: PropTypes.string,
    secondary: PropTypes.string,
    tertiary: PropTypes.string,
  }).isRequired,
  size: PropTypes.number,
};

const Field = ({ label, icon: Icon, value, onChange, type = 'text', readOnly, rightIcon: RightIcon, onRightIconClick, placeholder, error, required }) => (
  <label className="block space-y-1.5">
    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
      {label}
      {required && <span className="text-rose-500 ml-1">*</span>}
    </span>
    <span className="relative block">
      {Icon && (
        <span className={`absolute left-3 top-1/2 -translate-y-1/2 ${error ? 'text-rose-400' : 'text-slate-400'}`} aria-hidden="true">
          <Icon size={14} />
        </span>
      )}
      {type === 'textarea' ? (
        <textarea
          value={value || ''}
          onChange={onChange}
          readOnly={readOnly}
          placeholder={placeholder}
          className={`w-full min-h-[80px] rounded-lg bg-gray-50 border ${error ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none py-2 text-xs font-medium text-[#0d0d0d] transition-colors ${Icon ? 'pl-9 px-3' : 'px-3'} ${readOnly ? 'cursor-not-allowed !bg-gray-100' : ''}`}
        />
      ) : (
        <input
          type={type}
          value={value || ''}
          onChange={onChange}
          readOnly={readOnly}
          placeholder={placeholder}
          className={`w-full rounded-lg bg-gray-50 border ${error ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none py-2.5 text-xs font-medium text-[#0d0d0d] transition-colors ${Icon ? 'pl-9' : 'pl-3'} ${RightIcon ? 'pr-10' : 'pr-3'} ${readOnly ? 'cursor-not-allowed !bg-gray-100' : ''}`}
        />
      )}
      {type !== 'textarea' && RightIcon && (
        <button type="button" onClick={onRightIconClick} className={`absolute right-3 top-1/2 -translate-y-1/2 ${error ? 'text-rose-400' : 'text-slate-400'} hover:text-[var(--theme-primary)]`} aria-label={`Toggle ${label}`}>
          <RightIcon size={14} />
        </button>
      )}
    </span>
    {error && <p className="text-[11px] font-medium text-rose-500 mt-1">{error}</p>}
  </label>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  type: PropTypes.string,
  readOnly: PropTypes.bool,
  rightIcon: PropTypes.elementType,
  onRightIconClick: PropTypes.func,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  required: PropTypes.bool,
};

const formatName = (val) => val.replace(/[^a-zA-Z\s]/g, '').replace(/\b\w/g, c => c.toUpperCase());
const formatPhone = (val) => val.replace(/\D/g, '').slice(0, 10);

const calculateAge = (dobStr) => {
  if (!dobStr) return '';
  const dob = new Date(dobStr);
  if (isNaN(dob.getTime())) return '';
  const diff = Date.now() - dob.getTime();
  const ageDate = new Date(diff); 
  return Math.abs(ageDate.getUTCFullYear() - 1970).toString();
};

export const OrgAdminProfile = ({ org, user, showNotification, onUpdateUser, onUpdateOrg, isT0: isT0Prop, isT1: isT1Prop }) => {
  const [activeProfileTab, setActiveProfileTab] = useState('details');

  const isT0 = useMemo(() => {
    if (typeof isT0Prop === 'boolean') return isT0Prop;
    if (!org || !org.modules) return true;
    const activeMods = Object.keys(org.modules).filter(k => org.modules[k] && k !== 'pos' && k !== 'catalog');
    return activeMods.length === 0;
  }, [org, isT0Prop]);

  const isT1 = useMemo(() => {
    if (typeof isT1Prop === 'boolean') return isT1Prop;
    if (!org || !org.modules || isT0) return false;
    const { purchase, delivery, transport, warehouse } = org.modules;
    return !purchase && !delivery && !transport && !warehouse;
  }, [org, isT0, isT1Prop]);

  const isLockedTier = isT0 || isT1;

  const initial = useMemo(() => ({
    orgName: org.name,
    orgEmail: org.email,
    orgPhone: org.phone,
    orgAddress: org.address,
    orgGstin: org.gstin,
    userName: user?.name || 'Unknown',
    userRole: (user?.role === 'Organizer' || user?.role === 'org-admin') ? 'Organization Admin' : (user?.role || 'Member'),
    userEmail: user?.email || 'N/A',
    firstName: user?.firstName || '',
    middleName: user?.middleName || '',
    lastName: user?.lastName || '',
    gender: user?.gender || '',
    dob: user?.dob || '',
    age: user?.age || '',
    mobileNumber: user?.mobileNumber || user?.phone || '',
    altMobileNumber: user?.altMobileNumber || '',
    address: user?.address || '',
    state: user?.state || '',
    district: user?.district || '',
    pincode: user?.pinCode || '',
    additionalInfo: user?.additionalInfo || '',
    avatarUrl: user?.avatarUrl || null,
  }), [org, user]);

  const [draft, setDraft] = useState(initial);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  // Only reset when user identity changes; preserve locally selected avatar
  useEffect(() => {
    setDraft((prev) => ({
      ...initial,
      avatarUrl: prev.avatarUrl || initial.avatarUrl,
    }));
   
  }, [user?.id]);

  const userInitials = (draft.firstName || draft.userName || 'U').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();

  // Convert to base64 so it survives state resets and is storable in DB
  // One untouched photo here became a 16.8 MB avatarUrl, and /api/users carries
  // it on every load. An avatar is drawn at about forty pixels, so 256 is
  // already generous.
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const avatarUrl = await compressImage(file, { maxWidth: 256, maxHeight: 256 });
    setDraft((prev) => ({ ...prev, avatarUrl }));
  };

  const handleSave = async () => {
    const newErrors = {};

    const nameVal = (draft.firstName || '').trim();
    if (!nameVal) newErrors.firstName = 'Please fill the First Name.';
    else if (nameVal.length > 255) newErrors.firstName = 'First name must be 255 characters or less.';

    if (!(draft.lastName || '').trim()) newErrors.lastName = 'Last Name is required.';
    if (!(draft.address || '').trim()) newErrors.address = 'Address is required.';
    if (!(draft.state || '').trim()) newErrors.state = 'State is required.';
    if (!(draft.district || '').trim()) newErrors.district = 'District is required.';
    if (!(draft.pincode || '').trim()) newErrors.pincode = 'Pincode is required.';
    else if (!/^\d{6}$/.test((draft.pincode || '').trim())) newErrors.pincode = 'Pincode must be exactly 6 digits.';

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!draft.userEmail?.trim()) newErrors.userEmail = 'Please fill the Email Address.';
    else if (!emailPattern.test(draft.userEmail.trim())) newErrors.userEmail = 'Invalid email address.';

    const phoneRegex = /^\d{10}$/;
    if (!draft.mobileNumber?.trim()) newErrors.mobileNumber = 'Please fill the Mobile Number.';
    else if (!phoneRegex.test(draft.mobileNumber.replace(/\D/g, '').slice(-10))) {
      newErrors.mobileNumber = 'Must be exactly 10 digits.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (typeof showNotification !== 'undefined' && showNotification) showNotification('Please fix the highlighted fields.', 'error');
      return;
    }
    setErrors({});
    
    setSaving(true);
    try {
      const response = await fetchWithAuth('/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${draft.firstName || ''} ${draft.middleName ? draft.middleName + ' ' : ''}${draft.lastName || ''}`.trim() || draft.userName,
          email: draft.userEmail,
          initials: userInitials,
          phone: draft.mobileNumber,
          firstName: draft.firstName,
          middleName: draft.middleName,
          lastName: draft.lastName,
          gender: draft.gender,
          mobileNumber: draft.mobileNumber,
          altMobileNumber: draft.altMobileNumber,
          address: draft.address,
          state: draft.state,
          district: draft.district,
          pinCode: draft.pincode,
          additionalInfo: draft.additionalInfo,
          dob: draft.dob,
          age: draft.age,
          avatarColor: user?.avatarColor || '#0a5132',
          ...(draft.avatarUrl !== initial.avatarUrl && { avatarUrl: draft.avatarUrl || '' }),
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        if (showNotification) showNotification(data.error || 'Failed to update profile', 'error');
        return;
      }

      if (showNotification) showNotification('Profile updated successfully.', 'success');
      if (onUpdateUser) {
        onUpdateUser({
          ...user,
          name: data.name,
          email: data.email,
          phone: data.phone,
          firstName: data.firstName,
          lastName: data.lastName,
          initials: data.initials,
          avatarColor: data.avatarColor,
          avatarUrl: data.avatarUrl || draft.avatarUrl,
        });
      }
    } catch (err) {
      console.error('Profile update failed:', err);
      if (showNotification) showNotification(`Error: ${err.message || 'Cannot connect to server.'}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const accent = org.theme.primary;

  return (
    <motion.section
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="bg-white border border-[var(--theme-tertiary)] rounded-lg p-6 md:p-8 shadow-sm flex flex-col gap-8 w-full"
    >
      <div className="flex items-center gap-6 border-b border-gray-100 pb-0">
        <button
          onClick={() => setActiveProfileTab('details')}
          className={`px-4 py-3 text-xs font-bold uppercase tracking-[0.1em] transition-all border-b-2 -mb-[1px] ${activeProfileTab === 'details' ? 'border-[var(--theme-primary)] text-[var(--theme-primary)]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
        >
          PROFILE DETAILS
        </button>
        {org && (
          <button
            onClick={() => setActiveProfileTab('subscription')}
            className={`px-4 py-3 text-xs font-bold uppercase tracking-[0.1em] transition-all border-b-2 -mb-[1px] ${activeProfileTab === 'subscription' ? 'border-[var(--theme-primary)] text-[var(--theme-primary)]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
          >
            MY SUBSCRIPTION
          </button>
        )}
      </div>

      {activeProfileTab === 'details' ? (
        <>
          <header className="flex justify-center pb-8 pt-4">
            <div className="relative shrink-0">
              <div
                className="w-24 h-24 rounded-3xl flex items-center justify-center text-white text-2xl font-extrabold shadow-lg overflow-hidden relative"
                style={{
                  background: `linear-gradient(135deg, ${accent} 0%, color-mix(in srgb, ${accent} 70%, #000 30%) 100%)`,
                  boxShadow: `0 18px 36px -16px ${accent}`,
                }}
              >
                {draft.avatarUrl ? (
                  <img src={draft.avatarUrl} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  userInitials
                )}
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute -bottom-2 -right-2 w-8 h-8 bg-white border border-[var(--theme-tertiary)] rounded-full flex items-center justify-center text-slate-600 hover:text-[var(--theme-primary)] hover:border-[var(--theme-primary-tint)] transition-colors shadow-sm"
                aria-label="Upload profile image"
              >
                <Camera size={14} />
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
              />
            </div>
          </header>

          <div className="space-y-6">
        <section className="bg-slate-50/50 border border-gray-100 rounded-lg p-5">
          <h4 className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-4 flex items-center gap-2">
            <Building2 size={14} style={{ color: accent }} aria-hidden="true" /> Organization Details
          </h4>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-[11px]">
            <li className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white border border-gray-100 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]">
              <Building2 size={14} style={{ color: accent }} aria-hidden="true" />
              <span className="font-medium text-slate-700 truncate">{org.name}</span>
            </li>
            <li className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white border border-gray-100 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]">
              <Mail size={14} style={{ color: accent }} aria-hidden="true" />
              <span className="font-medium text-slate-700 truncate">{org.email}</span>
            </li>
            <li className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white border border-gray-100 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]">
              <Phone size={14} style={{ color: accent }} aria-hidden="true" />
              <span className="font-medium text-slate-700">{org.mobileNumber || org.phone}</span>
            </li>
            <li className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white border border-gray-100 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] md:col-span-2 lg:col-span-1">
              <MapPin size={14} style={{ color: accent }} aria-hidden="true" />
              <span className="font-medium text-slate-700 truncate">{org.address}</span>
            </li>
            <li className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white border border-gray-100 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]">
              <Hash size={14} style={{ color: accent }} aria-hidden="true" />
              <span className="font-medium text-slate-700">{org.gstin}</span>
            </li>
          </ul>
        </section>

        <section className="bg-slate-50/50 border border-gray-100 rounded-lg p-5">
          <h4 className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-4 flex items-center gap-2">
            <User size={14} style={{ color: accent }} aria-hidden="true" /> Your Account
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="First Name" icon={User} error={errors.firstName} required value={draft.firstName} onChange={(e) => { setDraft({ ...draft, firstName: formatName(e.target.value) }); if (errors.firstName) setErrors({ ...errors, firstName: undefined }); }} />
            <Field label="Middle Name" icon={User} value={draft.middleName} onChange={(e) => setDraft({ ...draft, middleName: formatName(e.target.value) })} />
            <Field label="Last Name" icon={User} error={errors.lastName} required value={draft.lastName} onChange={(e) => { setDraft({ ...draft, lastName: formatName(e.target.value) }); if (errors.lastName) setErrors({ ...errors, lastName: undefined }); }} />

            <Field label="Role" icon={ShieldCheck} value={draft.userRole} readOnly />
            <Field label="Email Address" icon={Mail} readOnly type="email" value={draft.userEmail} />
            <Field label="Mobile Number" icon={Phone} error={errors.mobileNumber} required type="tel" value={draft.mobileNumber} onChange={(e) => { setDraft({ ...draft, mobileNumber: formatPhone(e.target.value) }); if (errors.mobileNumber) setErrors({ ...errors, mobileNumber: undefined }); }} />
            
            <Field label="Alternate Mobile" icon={Phone} value={draft.altMobileNumber} onChange={(e) => setDraft({ ...draft, altMobileNumber: formatPhone(e.target.value) })} />
            
            <Field label="Gender" value={draft.gender || ''} readOnly />

            <Field label="DOB" value={draft.dob || ''} readOnly />

            <Field label="Age" type="number" value={draft.age} readOnly placeholder="Auto-calculated" />

            <div className="md:col-span-2">
              <Field label="Address" icon={MapPin} error={errors.address} required value={draft.address} onChange={(e) => { setDraft({ ...draft, address: e.target.value }); if (errors.address) setErrors({ ...errors, address: undefined }); }} />
            </div>

            <Field label="State" error={errors.state} required value={draft.state} onChange={(e) => { setDraft({ ...draft, state: e.target.value }); if (errors.state) setErrors({ ...errors, state: undefined }); }} />
            <Field label="District" error={errors.district} required value={draft.district} onChange={(e) => { setDraft({ ...draft, district: e.target.value }); if (errors.district) setErrors({ ...errors, district: undefined }); }} />
            <Field label="Pincode" error={errors.pincode} required value={draft.pincode} onChange={(e) => { setDraft({ ...draft, pincode: formatPincode(e.target.value) }); if (errors.pincode) setErrors({ ...errors, pincode: undefined }); }} />

          </div>
        </section>

        <section className="bg-slate-50/50 border border-gray-100 rounded-lg p-5">
          <h4 className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-4 flex items-center gap-2">
            <BookOpen size={14} style={{ color: accent }} aria-hidden="true" /> Additional Details
          </h4>
          <Field 
            label="Additional Information" 
            type="textarea" 
            value={draft.additionalInfo} 
            onChange={(e) => setDraft({ ...draft, additionalInfo: e.target.value })} 
            placeholder="Any other notes or information..." 
          />
        </section>
      </div>
          <div className="flex items-center justify-end gap-3 mt-auto pt-4 border-t border-gray-100">
            <button type="button" onClick={() => setDraft(initial)} className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:text-slate-700">Reset</button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white text-xs font-bold uppercase tracking-[0.14em] transition-colors shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
              style={{ background: accent, boxShadow: `0 12px 24px -12px ${accent}` }}
            >
              {saving && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </>
      ) : (
        <SubscriptionTab org={org} showNotification={showNotification} onRenewed={(updated) => onUpdateOrg && onUpdateOrg(updated)} />
      )}
    </motion.section>
  );
};

OrgAdminProfile.propTypes = {
  org: PropTypes.object.isRequired,
  user: PropTypes.object,
  showNotification: PropTypes.func,
  onUpdateUser: PropTypes.func,
};
